// ---------------------------------------------------------------------------
// THE CHANNEL THAT REACHES EVERYONE.
//
// Born from the Aug 19 outage: ~631 devices felt it, 9 had email addresses.
// Most Pleasant users are anonymous by design ("skip sign-in" is a feature),
// so email can never be the outage channel - this banner is. Morgan writes a
// row into app_notices; every app checks on launch and when it returns to
// the foreground, and shows the newest live notice. Dismissal is remembered
// PER NOTICE, so a new notice always shows once but nobody is nagged twice
// by the same one.
//
// Fails toward silence (rule 20's cousin): if the fetch errors, no banner -
// a broken notice system must never add noise to whatever else is broken.
// ---------------------------------------------------------------------------

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Megaphone, TriangleAlert, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { firstOpenAt } from "@/utils/firstOpen";

const DISMISS_KEY = "pleasant.notice.dismissed";

interface Notice {
  id: string;
  message: string;
  severity: "info" | "warning";
  created_at?: string;
}

interface Props {
  /** PLE-37: the banner is in normal flow, but the full-bleed map and the
   *  logo/search header are both absolutely positioned to the top of the
   *  screen, ignoring this sibling's height — so a shown notice floated on
   *  top of the header instead of pushing it down (z-40 over z-20), hiding
   *  Sign in / Settings behind it. Reporting the rendered height lets the
   *  caller offset those layers by exactly that amount; 0 when there's no
   *  notice, i.e. the normal case is unaffected. */
  onHeightChange?: (px: number) => void;
}

export const NoticeBanner = ({ onHeightChange }: Props = {}) => {
  const [notice, setNotice] = useState<Notice | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        // `app_notices` is absent from the generated Database type, so
        // `.from()` rejects the name outright. It is also absent from
        // supabase/migrations — the only mention of this table anywhere in
        // the repo is this file, so the schema lives solely in the hosted
        // project. Rather than hand-edit src/integrations/supabase/types.ts
        // (generated; the edit would vanish on the next regeneration) or
        // invent a migration for a table whose real columns I cannot see,
        // describe just the call we make and keep the rows typed.
        //
        // DURABLE FIX: add a migration for app_notices and regenerate the
        // types, after which this whole shim can go and `.from()` works.
        //
        // The .bind is load-bearing: `from` reads `this.rest`, so detached it
        // throws, the catch below swallows it, and no notice ever shows. That
        // silenced this banner everywhere from Sep 25 (98ef35c9) until
        // NoticeBanner.test.tsx caught it.
        const from = supabase.from.bind(supabase) as unknown as (table: string) => {
          select: (columns: string) => {
            order: (column: string, opts: { ascending: boolean }) => {
              limit: (n: number) => Promise<{ data: Notice[] | null; error: unknown }>;
            };
          };
        };
        const { data, error } = await from("app_notices")
          .select("id,message,severity,created_at")
          .order("created_at", { ascending: false })
          .limit(1);
        if (error || !alive) return;
        const n = data?.[0] ?? null;
        if (!n) { setNotice(null); return; }
        const dismissed = localStorage.getItem(DISMISS_KEY);
        if (dismissed === n.id) { setNotice(null); return; }
        // PLE-13: an "info" notice (e.g. what's new in 2.5) posted BEFORE this
        // device first opened Pleasant was written for existing users — a
        // brand-new install shouldn't be greeted by it (it was stacking with
        // the first-run intro strip, PLE-37). "warning" notices (outages)
        // still reach everyone, new or not.
        const posted = n.created_at ? Date.parse(n.created_at) : NaN;
        const installed = firstOpenAt();
        if (n.severity !== "warning" && installed > 0 && Number.isFinite(posted) && posted < installed) {
          setNotice(null);
          return;
        }
        setNotice(n);
      } catch { /* silence - see header */ }
    };
    void check();
    // Re-check when the app comes back to the foreground: a notice posted
    // mid-drive should be there when the phone wakes, without polling.
    const onVis = () => { if (document.visibilityState === "visible") void check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; document.removeEventListener("visibilitychange", onVis); };
  }, []);

  // Measure after paint (useLayoutEffect, not useEffect) so the header/map
  // never get one frame at the wrong offset. Re-measures on resize/reflow
  // (long messages wrap differently at different widths) via ResizeObserver.
  useLayoutEffect(() => {
    if (!notice) { onHeightChange?.(0); return; }
    const el = rootRef.current;
    if (!el) return;
    const report = () => onHeightChange?.(el.offsetHeight);
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [notice, onHeightChange]);

  if (!notice) return null;

  const warning = notice.severity === "warning";
  return (
    <div
      ref={rootRef}
      role="status"
      className={`relative z-40 flex items-start gap-2.5 px-4 py-2.5 text-sm leading-snug ${
        warning
          ? "bg-amber-50 text-amber-900 border-b border-amber-200"
          // Was bg-primary/10 — 10% opacity let the map/header show straight
          // through the banner (PLE-37: "bad transparency ... covers up top
          // of map and buttons"), so text and map tiles blended into an
          // unreadable mess instead of a legible banner. bg-mint-soft is the
          // same opaque-tint pattern as the warning branch above, just in
          // Pleasant's brand mint rather than amber.
          : "bg-mint-soft text-foreground border-b border-mint/30"
      }`}
    >
      {warning
        ? <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
        : <Megaphone className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />}
      <p className="flex-1">{notice.message}</p>
      <button
        type="button"
        aria-label="Dismiss notice"
        onClick={() => {
          try { localStorage.setItem(DISMISS_KEY, notice.id); } catch { /* fine */ }
          setNotice(null);
        }}
        className="shrink-0 -m-1 p-1 rounded hover:bg-black/5 touch-manipulation"
      >
        <X className="w-4 h-4" aria-hidden />
      </button>
    </div>
  );
};
